<template>
  <div data-component-group="filter-forms" data-component="payment">
    <FiltersForm
      has-phone-sheet
      :applied-filters="appliedFilters"
      :on-clear-filter="clearFilter"
      :has-filters="hasFilters"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="selectedCountries"
    >
      <template v-slot:phone-actions>
        <slot name="phone-actions" />
      </template>

      <v-text-field
        clearable
        v-model="search"
        type="search"
        label="Search"
        prepend-inner-icon="search"
      />

      <v-autocomplete
        clearable
        v-model="contract"
        :items="contracts"
        :loading="isLoadingContracts"
        prepend-inner-icon="article"
        :return-object="true"
        :item-title="(item) => String(item.id)"
        label="Contract ID"
        v-model:search="contractSearch"
      />

      <base-date-picker
        :date="date"
        placeholder="Dates"
        :config="dateConfig"
        :on-change="onChangeDate"
      />

      <v-autocomplete
        clearable
        v-model="status"
        :items="statuses"
        prepend-inner-icon="download_done"
        :return-object="true"
        item-title="name"
        label="Status"
        @focus="onFocusStatus"
      />

      <v-autocomplete
        clearable
        v-model="business"
        :items="businesses"
        :loading="isLoadingBusinesses"
        prepend-inner-icon="domain"
        label="Business"
        item-title="name"
        item-value="id"
        v-model:search="businessSearch"
        @update:model-value="isSelectingBusiness = true"
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import FiltersForm from './FiltersBase';
  import filterUrlSync from '@/mixins/filterUrlSync';
  import filterChips, { platformChips } from '@/mixins/filterChips';
  import isDateRange from '../../../validators/dateRange';
  import {isNum, isNull, debounce, isStr, escapeSearch} from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  const businessSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500,
  });

  // TODO: Translate component
  export default {
    mixins: [filterUrlSync, filterChips],
    components: {
      FiltersForm,
    },
    emits: ['clearFilters', 'filter'],
    data() {
      return {
        urlConfig: [
          { field: 'search', urlKey: 'search', type: 'string' },
          {
            field: 'selectedCountries', urlKey: 'countries', type: 'string[]',
          },
          { field: 'date', urlKey: 'date', type: 'string' },
          {
            field: 'status', urlKey: 'status', type: 'number', prop: 'id',
            fetchMethod: 'getStatuses', itemsField: 'statuses',
          },
          {
            // Both are looked up by id: the value the URL names can sit outside
            // the page the search fetch returns, and it has to end up among the
            // options or the select shows a bare id.
            field: 'contract', urlKey: 'contract', type: 'number', prop: 'id',
            fetchByIds: 'getContractsByIds',
          },
          { field: 'business', urlKey: 'business', type: 'number', fetchByIds: 'getBusinessesByIds' },
        ],
        search: null,
        contract: null,
        contractSearch: '',
        business: null,
        businesses: [],
        businessSearch: '',
        isSelectingBusiness: false,
        date: null,
        status: null,
        selectedCountries: [],
        dateConfig: {
          mode: 'range',
        },
        isLoadingContracts: false,
        isLoadingBusinesses: false,
        isRefreshingContracts: false,
      };
    },
    computed: {
      filterChipFields() {
        const platforms = this.$store.getters['users/countries'] || [];

        return [
          {
            model: 'selectedCountries',
            label: this.$t('platform'),
            toChips: platformChips(platforms),
          },
          { model: 'search', label: 'Search' },
          {
            model: 'contract',
            label: 'Contract ID',
            toChips: (contract) => [{ value: contract?.id }],
          },
          { model: 'date', label: 'Dates' },
          { model: 'status', label: 'Status' },
          {
            model: 'business',
            label: 'Business',
            toChips: (businessId) => [{
              value: this.businesses.find(({ id }) => id === businessId)?.name ?? businessId,
            }],
          },
        ];
      },
      hasFilters() {
        const {
          search,
          contract = {},
          date,
          status = {},
          business,
          selectedCountries = [],
        } = this;

        return (
          !!search ||
          isNum(contract?.id) ||
          !!date ||
          selectedCountries.length > 0 ||
          isNum(status?.id) ||
          isNum(business)
        );
      },
      contracts() {
        const items = this.$store.getters['contracts/items'];
        // A contract restored from the URL is looked up by id, so it is not in
        // the listing the store holds. Keep it among the options.
        return this.contract && !items.some(({ id }) => id === this.contract.id)
          ? [...items, this.contract]
          : items;
      },
      statuses() {
        return this.$store.getters['payments/statuses'];
      },
    },
    watch: {
      contract() {
        this.isRefreshingContracts = true;
      },
      businessSearch(nextSearch) {
        if (this.isSelectingBusiness) {
          this.isSelectingBusiness = false;
          return;
        }
        if (isStr(nextSearch)) {
          businessSearchDebouncer(() => this.getBusinesses(nextSearch));
        }
      },
      contractSearch(nextSearch) {
        if (!isNull(nextSearch)) {
          if (this.isRefreshingContracts) {
            this.isRefreshingContracts = false;
            return false;
          }

          debouncer(() => this.getContracts(nextSearch));
        }
      },
    },
    methods: {
      getBusinessesByIds(ids) {
        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItemsByIds', { ids })
          .then((items) => {
            // `businesses` is local data here, so the looked-up row has to be
            // put into it or the select has a value it cannot offer.
            this.businesses = [...this.businesses, ...items];
            return items;
          })
          .finally(() => {
            this.isLoadingBusinesses = false;
          });
      },
      getContractsByIds(ids) {
        this.isLoadingContracts = true;
        return this.$store
          .dispatch('contracts/getItemsByIds', { ids })
          .finally(() => {
            this.isLoadingContracts = false;
          });
      },
      getBusinesses(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingBusinesses = true;
        return this.$store.dispatch('businesses/getItems', query)
          .then(({ items }) => {
            this.businesses = items;
            this.isLoadingBusinesses = false;
            return items;
          })
          .catch((err) => {
            this.isLoadingBusinesses = false;
            return err;
          });
      },
      getStatuses() {
        return this.$store.dispatch('payments/getStatuses');
      },
      getContracts(search) {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingContracts = true;
        return this.$store
          .dispatch('contracts/getItems', query)
          .then((res) => {
            this.isLoadingContracts = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingContracts = false;
            return err;
          });
      },
      onFilter() {
        const {
          search,
          contract = {},
          date,
          status = {},
          business,
          selectedCountries = [],
        } = this;
        const formattedSearch = escapeSearch(search);
        const hasDate = !!date;
        let nextDate = date;

        if (hasDate && !isDateRange(nextDate)) {
          nextDate = `${nextDate} - ${nextDate}`;
        }

        this.syncFiltersToUrl();
        this.rememberAppliedFilters();

        this.$emit('filter', {
          business_id: business,
          search: formattedSearch,
          contract_id: contract?.id,
          countries: selectedCountries,
          created_at: nextDate,
          status: status?.id,
        });
      },
      onClearFilters() {
        this.search = null;
        this.contract = null;
        this.selectedCountries = [];
        this.date = null;
        this.status = null;
        this.business = null;

        this.clearFiltersUrl();
        this.forgetAppliedFilters();

        this.$emit('clearFilters');
      },
      onChangeDate(date) {
        this.date = date;
      },
      onFocusStatus() {
        if (this.statuses.length === 0) {
          this.getStatuses();
        }
      },
    },
  };
</script>
